/** Deck surfaces: brushed aluminium, platter felt, and the strobe dot band. */

import type * as THREE from 'three';
import { clamp, TAU } from '../lib/math.ts';
import { mulberry32 } from '../lib/random.ts';
import { canvas, ctx2d, memo, tex } from './canvas.ts';

type Renderer = THREE.WebGLRenderer | null;

export interface BrushedMaps {
  roughnessMap: THREE.CanvasTexture;
}

/**
 * Radial brushing: noise constant along a ring but varying with angle. The two
 * noise octaves collapse into one lookup so the per-pixel loop is just an
 * atan2 and an array read.
 */
export function brushedMetalMaps(size = 1024, renderer: Renderer = null): BrushedMaps {
  return memo(`brushed-${size}`, () => {
    const c = canvas(size);
    const g = ctx2d(c, { willReadFrequently: true });
    const img = g.createImageData(size, size);
    const d = img.data;
    const R = size / 2;
    const rand = mulberry32(19);
    const ANG = 2048;

    const streaks = new Float32Array(ANG);
    for (let i = 0; i < ANG; i++) streaks[i] = rand();
    const band = new Float32Array(ANG);
    for (let i = 0; i < ANG; i++) {
      const prev = streaks[(i - 1 + ANG) % ANG] as number;
      const next = streaks[(i + 1) % ANG] as number;
      const s = (prev + (streaks[i] as number) * 2 + next) / 4;
      band[i] = s * 0.62 + (streaks[(i * 7) % ANG] as number) * 0.22;
    }

    const INV = ANG / TAU;
    for (let y = 0; y < size; y++) {
      const dy = y - R;
      const dy2 = dy * dy;
      for (let x = 0; x < size; x++) {
        const dx = x - R;
        const idx = (((Math.atan2(dy, dx) + Math.PI) * INV) | 0) % ANG;
        const r = Math.sqrt(dx * dx + dy2) / R;
        const rough = 118 + ((band[idx] as number) + rand() * 0.16) * 54 - r * 10;
        const i = (y * size + x) * 4;
        const v = clamp(rough, 0, 255);
        d[i] = v;
        d[i + 1] = v;
        d[i + 2] = v;
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return { roughnessMap: tex(c, { aniso: 16, renderer }) };
  });
}

/** Felt slipmat: blurred noise turned into a normal map so the fibres clump. */
export function feltNormalMap(size = 512, renderer: Renderer = null): THREE.CanvasTexture {
  return memo(`felt-${size}`, () => {
    const c = canvas(size);
    const g = ctx2d(c, { willReadFrequently: true });
    const img = g.createImageData(size, size);
    const d = img.data;
    const rand = mulberry32(53);

    const height = new Float32Array(size * size);
    for (let i = 0; i < height.length; i++) height[i] = rand();

    const blur = new Float32Array(size * size);
    const at = (buf: Float32Array, x: number, y: number): number =>
      buf[((y + size) % size) * size + ((x + size) % size)] as number;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        let s = 0;
        for (let k = -1; k <= 1; k++) for (let j = -1; j <= 1; j++) s += at(height, x + j, y + k);
        blur[y * size + x] = s / 9;
      }
    }

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const nx = (at(blur, x - 1, y) - at(blur, x + 1, y)) * 1.6;
        const ny = (at(blur, x, y - 1) - at(blur, x, y + 1)) * 1.6;
        const len = Math.hypot(nx, ny, 1);
        const i = (y * size + x) * 4;
        d[i] = (nx / len) * 127.5 + 127.5;
        d[i + 1] = (ny / len) * 127.5 + 127.5;
        d[i + 2] = (1 / len) * 127.5 + 127.5;
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return tex(c, { repeat: [5, 5], renderer });
  });
}

/** Strobe dot band around the platter rim, rendered on the cylinder side. */
export function strobeBandMap(renderer: Renderer = null): THREE.CanvasTexture {
  return memo('strobe', () => {
    const w = 2048;
    const h = 64;
    const c = canvas(w, h);
    const g = ctx2d(c);
    g.fillStyle = '#9aa2a8';
    g.fillRect(0, 0, w, h);
    g.fillStyle = 'rgba(255,255,255,0.55)';
    g.fillRect(0, 0, w, 6);

    for (const row of [
      { y: 20, n: 100 },
      { y: 42, n: 92 },
    ]) {
      for (let i = 0; i < row.n; i++) {
        const x = (i + 0.5) * (w / row.n);
        g.fillStyle = '#1b1f22';
        g.beginPath();
        g.roundRect(x - 4.5, row.y - 5, 9, 10, 3);
        g.fill();
      }
    }
    return tex(c, { srgb: true, repeat: [1, 1], renderer });
  });
}
